import React from 'react';
import classnames from 'classnames';
import {
    ButtonBase,
    List as MaterialList,
    ListItem,
    SvgIcon,
    Typography,
} from '@material-ui/core';
import {Delete} from '@material-ui/icons';
import wrap from 'wraptile';
import {curry} from 'ramda';
import moment from 'moment';

import {
    OnMouseButtonEvent,
    OnClickEvent,
    OnSelectChangeEvent,
    OnMouseDivEvent,
} from '@pluto/@types';
import {TimespanCategory} from '@pluto/rest';
import {
    frameToSMPTE,
    FrameRate,
} from '@accurate-player/accurate-player-core';
import QCTooltip from '@pluto/qc-tooltip';
import {QCReportMarker} from 'VideoAudioQC/Timeline/TimelineTypes';
import TimecodeInput from 'VideoAudioQC/QCTabs/TimecodeInput/TimecodeInputContainer';
import TextInput from 'VideoAudioQC/QCTabs/TextInput/TextInputContainer';
import {
    GoToStart,
    GoToEnd,
    MoveMarker,
} from 'VideoAudioQC/icons';
import {
    getCategoryLabel,
    getQCReportMenuItems,
} from '../../IssueSelectMenuItems/IssueSelectMenuItems';
import QCTabsSelect, {SelectMenuGroup} from '../../QCTabsSelect/QCTabsSelect';
import {
    ListItemRef,
    OnAddRemoveTimecodes,
    OnButtonClick,
    OnEditMarker,
    OnMoveTime,
    OnRowClick,
} from './ListContainer';

import './List.scss';

export interface ListProps {
    markers: QCReportMarker[];
    noteTypes: TimespanCategory[];
    selectedMarkerId: string;
    frameRate: FrameRate;
    dropFrame: boolean;
    duration: number;
    selectedColumn: string;
    selectedItemRef: ListItemRef;
    onRowClick: OnRowClick;
    onDeleteClick: OnButtonClick;
    onEditMarkerIn: OnMoveTime;
    onEditMarkerOut: OnMoveTime;
    onGoToStartClick: OnButtonClick;
    onGoToEndClick: OnButtonClick;
    onEditMarker: OnEditMarker;
    onAddRemoveTimecodes: OnAddRemoveTimecodes;
    jobDeliveryDate: string;
}

export interface InputFieldProps {
    selectedMarkerId: string,
    selectedColumn: string,
    columnKey: string,
    noteTypes: TimespanCategory[],
    onInputClick: OnClickEvent,
    onEditMarker: OnEditMarker,
    onRowClick: OnRowClick,
}

export interface QCNoteData extends QCReportMarker {
    startSMPTE: string;
    endSMPTE: string;
}

export const getInOutPoint = (time: number | undefined, frameRateValue: number, dropFrame: boolean) => {
    if (time === undefined)
        return '';
    
    return frameToSMPTE(time, frameRateValue, dropFrame);
};

const mapQCNoteData = curry((props: ListProps, qcNote: QCReportMarker): QCNoteData => {
    const {
        frameRate,
        dropFrame,
    } = props;
    const {
        numerator,
        denominator,
    } = frameRate;
    const {
        start,
        end,
    } = qcNote;
    
    const frameRateValue = numerator / denominator;
    const startSMPTE = getInOutPoint(start, frameRateValue, dropFrame);
    const endSMPTE = getInOutPoint(end, frameRateValue, dropFrame);
    
    return {
        ...qcNote,
        startSMPTE,
        endSMPTE,
    };
});

export const onInputClick = (event: React.MouseEvent) => {
    event.stopPropagation();
};

export type OnLabelClickProps = {
    columnKey: string,
    noteData: QCNoteData,
    onRowClick: OnRowClick
}

export const onLabelClick = curry((props: OnLabelClickProps, event: OnMouseDivEvent) => {
    const {
        columnKey,
        noteData,
        onRowClick,
    } = props;
    
    event.stopPropagation();
    onRowClick(noteData, columnKey);
});

export const onIssueChange = wrap((data: QCNoteData, onEditMarker: OnEditMarker, event: OnSelectChangeEvent) => {
    const updatedIssue = event.target.value as number;
    const {issue} = data;
    
    if (updatedIssue === issue)
        return;
    
    const marker: QCReportMarker = {
        ...data,
        issue: updatedIssue,
    };
    
    onEditMarker({
        marker,
    });
});

const emptyLabel = 'No reason selected';

export const getIssueField = (issueFieldProps: InputFieldProps, noteData: QCNoteData, jobDeliveryDate: string) => {
    const {
        selectedMarkerId,
        selectedColumn,
        columnKey,
        noteTypes,
        onEditMarker,
        onRowClick,
    } = issueFieldProps;
    const {
        identifier,
        issue,
    } = noteData;
    
    if (selectedMarkerId === identifier) {
        // focus on this element if it has been clicked or as default when no particular column has been clicked.
        const isClicked = selectedColumn === columnKey || selectedColumn === '';
        const issues: SelectMenuGroup[] = getQCReportMenuItems(noteTypes);
        const handleOnChange = onIssueChange(noteData, onEditMarker);
        
        return (
            <QCTabsSelect
                onChange={handleOnChange}
                menuItems={issues}
                currentValue={issue}
                autoFocus={isClicked}
                emptyLabel={emptyLabel}
                id="issue-select"
                variant="filled"
            />
        );
    }
    
    const label = getCategoryLabel(issue, noteTypes, emptyLabel);
    const handleClick = onLabelClick({
        columnKey,
        noteData,
        onRowClick,
    });
    
    return (
        <Typography
            onClick={!jobDeliveryDate ? handleClick : () => {}}
            className="issue-text"
            variant="body2"
        >
            {label}
        </Typography>
    );
};

export const onDetailsChange = wrap((data: QCNoteData, onEditMarker: OnEditMarker, updatedDetails: string) => {
    const {details} = data;
    
    if (updatedDetails === details)
        return;
    
    const marker: QCReportMarker = {
        ...data,
        details: updatedDetails,
    };
    
    onEditMarker({
        marker,
    });
});

export const getDetailsField = (detailsFieldProps: InputFieldProps, noteData: QCNoteData) => {
    const {
        selectedMarkerId,
        selectedColumn,
        columnKey,
        onInputClick,
        onEditMarker,
        onRowClick,
    } = detailsFieldProps;
    
    if (selectedMarkerId === noteData.identifier) {
        const isClicked = selectedColumn === columnKey;
        const handleOnChange = onDetailsChange(noteData, onEditMarker);
        
        return (
            <TextInput
                onInputChange={handleOnChange}
                value={noteData.details}
                onClick={onInputClick}
                autoFocus={isClicked}
                variant="filled"
                multiline
                rowsMax={3}
            />
        );
    }
    
    const handleClick = onLabelClick({
        columnKey,
        noteData,
        onRowClick,
    });
    
    return (
        <Typography
            onClick={handleClick}
            variant="body2"
            className="details-text"
            color="textSecondary"
        >
            {noteData.details}
        </Typography>
    );
};

export interface TimecodeFieldProps {
    dropFrame: boolean,
    duration: number,
    frameRateValue: number,
    selectedMarkerId: string,
    selectedColumn: string,
    columnKey: string,
    onEditMarker: OnEditMarker,
    onInputClick: OnClickEvent,
    onRowClick: OnRowClick,
    onMoveMarkerTime: OnMoveTime,
    onGoToStartClick: OnButtonClick,
    onGoToEndClick: OnButtonClick,
}

export const onStartFrameChange = wrap((onEditMarker: OnEditMarker, data: QCNoteData, updatedStart: number) => {
    const {start} = data;
    
    if (updatedStart === start)
        return;
    
    const marker: QCReportMarker = {
        ...data,
        start: updatedStart,
    };
    
    onEditMarker({
        marker,
        inpoint: true,
    });
});

export const getStartTimecodeField = (props: TimecodeFieldProps, noteData: QCNoteData, jobDeliveryDate: string) => {
    const {
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        selectedColumn,
        columnKey,
        onInputClick,
        onEditMarker,
        onRowClick,
        onGoToStartClick,
        onMoveMarkerTime,
    } = props;
    const {
        identifier,
        start,
        startSMPTE,
    } = noteData;
    
    if (start === undefined)
        return '';
    
    if (selectedMarkerId === identifier) {
        const isClicked = selectedColumn === columnKey;
        const handleFrameChange = onStartFrameChange(onEditMarker, noteData);
        const handleMoveClick = onMoveTimecode(onMoveMarkerTime, noteData, 'startSMPTE');
        const handleGoToClick = onButtonClick(noteData, onGoToStartClick);
        
        return (
            <div>
                <TimecodeInput
                    frame={start}
                    dropFrame={dropFrame}
                    duration={duration}
                    frameRateValue={frameRateValue}
                    autoFocus={isClicked}
                    variant="filled"
                    onFrameChange={handleFrameChange}
                    onClick={onInputClick}
                />
                <QCTooltip
                    title="Move Start to CTI (S)"
                    arrow
                >
                    <ButtonBase
                        onClick={handleMoveClick}
                        aria-label="Move Start to CTI"
                    >
                        <MoveMarker />
                    </ButtonBase>
                </QCTooltip>
                <QCTooltip
                    title="Go to Start (Shift + S)"
                    arrow
                >
                    <ButtonBase
                        onClick={handleGoToClick}
                        aria-label="Go to Start"
                    >
                        <GoToStart />
                    </ButtonBase>
                </QCTooltip>
            </div>
        );
    }
    
    const handleClick = onLabelClick({
        columnKey,
        noteData,
        onRowClick,
    });
    
    return (
        <Typography
            className="start-smpte"
            onClick={!jobDeliveryDate ? handleClick : () => {}}
            variant="caption"
            color="textSecondary"
        >
            {startSMPTE}
        </Typography>
    );
};

export const onEndFrameChange = wrap((onEditMarker: OnEditMarker, data: QCNoteData, updatedEnd: number) => {
    const {end} = data;
    
    if (updatedEnd === end)
        return;
    
    const marker: QCReportMarker = {
        ...data,
        end: updatedEnd,
    };
    
    onEditMarker({
        marker,
        inpoint: false,
    });
});

export const onAddTimecodes = curry((onAddRemoveTimecodes: OnAddRemoveTimecodes, data: QCNoteData, event: OnMouseButtonEvent) => {
    onAddRemoveTimecodes(data, 'startSMPTE', true, event);
});

export const onRemoveTimecodes = curry((onAddRemoveTimecodes: OnAddRemoveTimecodes, data: QCNoteData, event: OnMouseButtonEvent) => {
    onAddRemoveTimecodes(data, 'issue', false, event);
});

export const onMoveTimecode = curry((onMoveTime: OnMoveTime, data: QCNoteData, column: string, event: OnMouseButtonEvent) => {
    onMoveTime(data, column, event);
});

export const getEndTimecodeField = (props: TimecodeFieldProps, noteData: QCNoteData, jobDeliveryDate: string) => {
    const {
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        selectedColumn,
        columnKey,
        onInputClick,
        onEditMarker,
        onRowClick,
        onMoveMarkerTime,
        onGoToEndClick,
    } = props;
    const {
        identifier,
        end,
        endSMPTE,
    } = noteData;
    
    if (end === undefined)
        return '';
    
    if (selectedMarkerId === identifier) {
        const isClicked = selectedColumn === columnKey;
        const handleFrameChange = onEndFrameChange(onEditMarker, noteData);
        const handleMoveClick = onMoveTimecode(onMoveMarkerTime, noteData, 'endSMPTE');
        const handleGoToClick = onButtonClick(noteData, onGoToEndClick);
        
        return (
            <div className="timecode-container">
                <TimecodeInput
                    frame={end}
                    dropFrame={dropFrame}
                    duration={duration}
                    frameRateValue={frameRateValue}
                    autoFocus={isClicked}
                    variant="filled"
                    onFrameChange={handleFrameChange}
                    onClick={onInputClick}
                />
                <QCTooltip
                    title="Move End to CTI (E)"
                    arrow
                >
                    <ButtonBase
                        onClick={handleMoveClick}
                        aria-label="Move End to CTI"
                    >
                        <MoveMarker />
                    </ButtonBase>
                </QCTooltip>
                <QCTooltip
                    title="Go to End (Shift + E)"
                    arrow
                >
                    <ButtonBase
                        onClick={handleGoToClick}
                        aria-label="Go to End"
                    >
                        <GoToEnd />
                    </ButtonBase>
                </QCTooltip>
            </div>
        );
    }
    
    const handleClick = onLabelClick({
        columnKey,
        noteData,
        onRowClick,
    });
    
    return (
        <Typography
            onClick={!jobDeliveryDate ? handleClick : () => {}}
            variant="caption"
            color="textSecondary"
        >
            {endSMPTE}
        </Typography>
    );
};

export const getRemoveTimecodes = (noteData: QCNoteData, selected: boolean, onAddRemoveTimecodes: OnAddRemoveTimecodes) => {
    if (!selected)
        return null;
    
    const handleRemoveClick = onRemoveTimecodes(onAddRemoveTimecodes, noteData);
    
    return (
        <div
            className="add-remove-timecode-text"
            role="button"
            onClick={handleRemoveClick}
        >
            <Typography variant="caption">Remove time codes</Typography>
        </div>
    );
};

export const getTimeFields = (props: ListProps, noteData: QCNoteData, jobDeliveryDate: string) => {
    const {
        dropFrame,
        duration,
        frameRate,
        selectedColumn,
        selectedMarkerId,
        onEditMarker,
        onRowClick,
        onAddRemoveTimecodes,
        onGoToStartClick,
        onGoToEndClick,
        onEditMarkerIn,
        onEditMarkerOut,
    } = props;
    const {
        identifier,
        start,
        end,
    } = noteData;
    const {
        numerator,
        denominator,
    } = frameRate;
    const frameRateValue = numerator / denominator;
    const selected = selectedMarkerId === identifier;
    
    if (selected && (start === undefined || end === undefined)) {
        const handleAddClick = onAddTimecodes(onAddRemoveTimecodes, noteData);
        
        return (
            <div
                className="add-remove-timecode-text"
                role="button"
                onClick={handleAddClick}
            >
                <Typography variant="caption">Add time codes</Typography>
            </div>
        );
    }
    
    const startField = getStartTimecodeField({
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        selectedColumn,
        columnKey: 'startSMPTE',
        onEditMarker,
        onInputClick,
        onRowClick,
        onGoToStartClick,
        onGoToEndClick,
        onMoveMarkerTime: onEditMarkerIn,
    }, noteData, jobDeliveryDate);
    
    const endField = getEndTimecodeField({
        dropFrame,
        duration,
        frameRateValue,
        selectedMarkerId,
        selectedColumn,
        columnKey: 'endSMPTE',
        onEditMarker,
        onInputClick,
        onRowClick,
        onGoToStartClick,
        onGoToEndClick,
        onMoveMarkerTime: onEditMarkerOut,
    }, noteData, jobDeliveryDate);
    
    const removeTimecodes = getRemoveTimecodes(noteData, selected, onAddRemoveTimecodes);
    
    return (
        <>
            {startField}
            {endField}
            {removeTimecodes}
        </>
    );
};

export const onButtonClick = curry((markerData: QCNoteData, onClick: OnButtonClick, event: OnMouseButtonEvent) => {
    event.stopPropagation();
    event.preventDefault();
    
    onClick(markerData);
});

export const getDeleteButton = (onDeleteClick: OnButtonClick, markerData: QCNoteData) => {
    const handleDeleteClick = onButtonClick(markerData, onDeleteClick);
    
    return (
        <QCTooltip
            title="Delete Note"
            arrow
        >
            <ButtonBase
                onClick={handleDeleteClick}
                aria-label="Delete Note"
            >
                <Delete />
            </ButtonBase>
        </QCTooltip>
    );
};

export const getListItem = curry((props: ListProps, noteData: QCNoteData) => {
    const {
        selectedItemRef,
        noteTypes,
        selectedColumn,
        selectedMarkerId,
        onEditMarker,
        onDeleteClick,
        onRowClick,
        jobDeliveryDate,
    } = props;
    const {
        identifier,
        createdByUser,
        updatedAt,
    } = noteData;
    
    const detailsField = getDetailsField({
        columnKey: 'details',
        noteTypes,
        selectedColumn,
        selectedMarkerId,
        onEditMarker,
        onInputClick,
        onRowClick,
    }, noteData);
    
    const issueFieldProps: InputFieldProps = {
        noteTypes,
        selectedMarkerId,
        selectedColumn,
        columnKey: 'issue',
        onInputClick,
        onEditMarker,
        onRowClick,
    };
    const issueField = getIssueField(issueFieldProps, noteData, jobDeliveryDate);
    
    const timefields = getTimeFields(props, noteData, jobDeliveryDate);
    
    const deleteButton = getDeleteButton(onDeleteClick, noteData);
    const selected = identifier === selectedMarkerId;
    
    const handleClick = onLabelClick({
        columnKey: 'issue',
        noteData,
        onRowClick,
    });
    
    const timeAgo = moment(updatedAt).fromNow();
    
    return (
        <ListItem
            key={identifier}
            selected={selected}
            onClick={!jobDeliveryDate ? handleClick : () => {}}
            innerRef={selected ? selectedItemRef : null}
        >
            <div className="heading">
                <Typography
                    variant="caption"
                    className="user-name"
                >
                    {createdByUser?.name}
                </Typography>
                <Typography
                    variant="caption"
                    color="textSecondary"
                >
                    {timeAgo}
                </Typography>
                {!jobDeliveryDate && deleteButton}
            </div>
            <div className="content">
                <div className="issue-detail-container">
                    {issueField}
                    {detailsField}
                </div>
                <div
                    className={classnames('timestamps', {
                        selected,
                    })}
                >
                    {timefields}
                </div>
            </div>
        </ListItem>
    );
});

export const getEmptyList = () => {
    return (
        <div className="empty">
            <SvgIcon>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 32 32"
                >
                    <path d="M25,4V19l-9,9L7,19V4Z" />
                </svg>
            </SvgIcon>
            <Typography
                variant="body1"
                color="textSecondary"
            >
                No notes added yet
            </Typography>
        </div>
    );
};

export const getList = (props: ListProps) => {
    const {markers} = props;
    
    const handleDataMap = mapQCNoteData(props);
    const data = markers.map(handleDataMap);
    
    const handleMapListItem = getListItem(props);
    const listItems = data.map(handleMapListItem);
    
    if (listItems.length === 0)
        return getEmptyList();
    
    return (
        <MaterialList>
            {listItems}
        </MaterialList>
    );
};

export default function List(props: ListProps) {
    const list = getList(props);
    
    return (
        <div className="qc-note-list-container">
            {list}
        </div>
    );
}
